<script lang="ts" setup>
import { computed, onMounted, ref, toRefs } from 'vue';
import { vMaska } from 'maska';

const props = withDefaults(
  defineProps<{
    modelValue: string | number | null | undefined;
    title?: string;
    note?: string;
    error?: string;
    placeholder?: string;
    focus?: boolean;
    maxLength?: number;
    mode?: 'text' | 'numeric' | 'tel' | 'none';
    type?: 'text' | 'number';
    mask?: string;
  }>(),
  {
    maxLength: undefined,
    mode: 'text',
    mask: undefined,
  },
);
const {
  type,
  mode,
  maxLength,
  error,
  focus,
  mask,
  note,
  placeholder,
  title,
  modelValue,
} = toRefs(props);

const emit = defineEmits(['update:modelValue', 'blur']);

const input = computed({
  get: () => {
    return modelValue.value;
  },
  set: (newValue) => {
    emit('update:modelValue', newValue);
  },
});

const inputRef = ref<HTMLInputElement>();

onMounted(() => {
  if (focus.value) {
    inputRef.value?.focus();
  }
});

defineExpose({ input });
</script>

<template>
  <BaseField :label="title" :error="error" :class="{ 'non-spin': mode === 'numeric' || mode === 'tel' }">
    <template v-if="mask">
      <input
        ref="inputRef"
        v-model="input"
        :placeholder="placeholder"
        inputmode="text"
        :type="type"
        class="input"
        :maxlength="maxLength"
        @blur="(e) => emit('blur', e)"
        v-maska
        :data-maska="mask"
      />
    </template>
    <template v-else>
      <input
        ref="inputRef"
        v-model="input"
        :placeholder="placeholder"
        inputmode="text"
        :type="type"
        class="input"
        :maxlength="maxLength"
        @blur="(e) => emit('blur', e)"
      />
    </template>
    <slot />
    <template #note>
      {{ note }}
    </template>

    <template v-if="maxLength ?? 0 > 0" #note-append>{{ `${input?.toString().length} / ${maxLength}` }}</template>
  </BaseField>
</template>

<style lang="scss" scoped>
.input {
  border: none;
  width: 100%;

  &:focus {
    outline: none;
  }

  color: $typography-paragraph;
  font-size: $typography-paragraph-size;

  span {
    @include adaptive-font(12, 10, 1600);
    margin-left: 12px;
  }
}

.non-spin {
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type='number'] {
    -moz-appearance: textfield;
  }
}
</style>
